/**
 * Editorial Authoring V2 — shared media picker (BUILD 2 extraction).
 *
 * Split out of EditorialSpreadWorkspaceV2.jsx so both the generic V2
 * workspace body and EditorialHeroV2.jsx can reuse the same
 * "Из Медиатеки" picker and the same two media endpoint paths without
 * an import cycle between those two files (Hero needs the picker;
 * the workspace needs Hero). No import from @/components/editorial/ —
 * GET /admin/media + mediaRecordUrl only.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";

export const MEDIA_LIST = "/admin/media";
export const MEDIA_UPLOAD = "/admin/media/upload-file";

// Transport-only thumbnail, never alters selection or stored media metadata.
function MediaLibraryThumbnailV2({ url }) {
  const src = useEditorialMediaUrl(url);
  return src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null;
}

export function MediaLibraryPickerV2({ open, onClose, onPick, accessZone = "public" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(MEDIA_LIST);
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        setItems(records);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  // New files are scoped to the selected publication venue. Historic
  // unclassified assets remain selectable until migration is authorized.
  const scoped = items.filter((m) => !m.access_zone || m.access_zone === accessZone);
  const filtered = q
    ? scoped.filter((m) => (m.filename || m.title || m.name || "").toLowerCase().includes(q))
    : scoped;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-media-library">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-center justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="font-serif text-lg text-espresso">Медиатека · {accessZone === "paid" ? "Закрытые фото" : "Открытые фото"}</div>
          <button type="button" onClick={onClose} className="text-sm text-espresso/60 hover:text-espresso"
            data-testid="editorial-v2-media-library-close">
            Закрыть
          </button>
        </div>
        <div className="px-5 pt-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по названию файла"
            className="w-full rounded-lg border border-espresso/20 bg-white px-3 py-2 text-sm outline-none focus:border-espresso/45"
            data-testid="editorial-v2-media-library-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="py-8 text-center text-sm text-espresso/60">Загрузка…</div>}
          {!loading && filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-espresso/60">Ничего не найдено.</div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {filtered.map((m) => {
                const url = mediaRecordUrl(m) || m.file_url || m.url;
                return (
                  <button key={m.id} type="button" onClick={() => onPick(m)}
                    className="aspect-square overflow-hidden rounded-lg border border-espresso/15 bg-white hover:border-espresso/40"
                    data-testid={`editorial-v2-media-library-item-${m.id}`}>
                    {url ? (
                      <MediaLibraryThumbnailV2 url={url} />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-espresso/40">
                        Фото
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
